export const stableSerializeCartRequest = (value) => {
  const seen = new WeakSet();
  const normalize = (entry) => {
    if (entry === null || entry === undefined) return entry ?? null;
    if (entry instanceof Date) return entry.toISOString();
    if (Array.isArray(entry)) return entry.map(normalize);
    if (typeof entry !== "object") return entry;
    if (seen.has(entry)) return "[circular]";
    seen.add(entry);
    const result = {};
    Object.keys(entry)
      .sort()
      .forEach((key) => {
        const nested = entry[key];
        if (nested !== undefined && typeof nested !== "function") {
          result[key] = normalize(nested);
        }
      });
    seen.delete(entry);
    return result;
  };
  return JSON.stringify(normalize(value));
};

export const buildCartRefreshInputKey = (item = {}, key = "") => {
  const normalizeSelections = (entries = []) => (Array.isArray(entries) ? entries : [])
    .map((entry = {}) => ({
      id: entry.id || entry.key || entry.code || entry.label || "",
      quantity: Number(entry.quantity || entry.qty || entry.count || 1) || 1,
      selected: entry.selected !== false && entry.included !== false,
      chargeableDays: Number(entry.chargeableDays || 0) || 0,
      skip: Boolean(entry.skip),
    }))
    .filter((entry) => entry.id)
    .sort((left, right) => String(left.id).localeCompare(String(right.id)));
  const slot = item?.slot || item?.options?.slot || {};
  const vehicle = item?.vehicle || item?.options?.vehicle || {};
  const referral = item?.referralContext || item?.metadata?.referralContext || {};
  return stableSerializeCartRequest({
    key,
    experienceType: item?.experienceType || item?.type || "",
    identifier: item?.identifier || item?.id || "",
    destination: item?.destinationSlug || item?.destination || item?.metadata?.destination || "",
    camp: item?.campSlug || item?.camp || item?.metadata?.camp || "",
    roomType: item?.roomTypeSlug || item?.roomType || item?.stayRoom || item?.metadata?.roomType || "",
    travelDate: item?.travelDate || item?.date || item?.metadata?.travelDate || item?.metadata?.date || "",
    checkIn: item?.checkIn || item?.metadata?.checkIn || "",
    checkOut: item?.checkOut || item?.metadata?.checkOut || item?.metadata?.checkout || "",
    quantity: Number(item?.quantity || item?.rooms || item?.guestCount || 1) || 1,
    ratePlanId: item?.ratePlanId || item?.ratePlan?.id || item?.options?.ratePlanId || "",
    entryPoint: item?.entryPoint || item?.options?.entryPoint || "",
    slot: slot.id || slot.key || slot.label || "",
    vehicle: vehicle.id || vehicle.key || vehicle.label || "",
    guestMix: item?.guestMix || item?.options?.guestMix || null,
    groupSelection: item?.groupSelection || item?.metadata?.groupSelection || item?.options?.groupSelection || null,
    addOns: normalizeSelections(item?.addOns || item?.options?.addOns),
    selectedCharges: normalizeSelections(item?.selectedCharges || item?.options?.selectedCharges),
    requiredCharges: normalizeSelections(item?.requiredCharges || item?.options?.requiredCharges),
    couponCode: item?.couponCode || item?.metadata?.couponCode || item?.options?.couponCode || "",
    referral: {
      affiliateCode: referral.affiliateCode || item?.affiliateCode || "",
      campaignCode: referral.campaignCode || item?.campaignCode || "",
      linkCode: referral.linkCode || item?.linkCode || "",
    },
    bookingIntent: item?.bookingIntent || null,
  });
};

export const cartItemNeedsQuoteRefresh = (item = {}, now = Date.now()) => {
  if (!item || typeof item !== "object") return true;
  if (item.stale) return true;
  if (item?.serverTotals?.authoritative !== true || item?.serverTotals?.status !== "verified") {
    return true;
  }
  const quoteCode = item.quoteCode || item?.quote?.quoteCode || item?.quoteMeta?.quoteCode || "";
  if (!quoteCode) return true;
  const expiresAt = item?.quoteMeta?.expiresAt || item?.quote?.expiresAt || null;
  if (!expiresAt) return false;
  const expiryMs = new Date(expiresAt).getTime();
  return !Number.isFinite(expiryMs) || expiryMs <= Number(now);
};

export const cartNeedsQuoteRefresh = (items = [], now = Date.now()) =>
  (Array.isArray(items) ? items : []).some((item) => cartItemNeedsQuoteRefresh(item, now));

export const runTasksWithConcurrencyLimit = async (tasks = [], requestedLimit = 2) => {
  const queue = Array.isArray(tasks) ? tasks.filter((task) => typeof task === "function") : [];
  if (!queue.length) return [];
  const limit = Math.max(1, Math.min(queue.length, Math.floor(Number(requestedLimit) || 1)));
  const results = new Array(queue.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < queue.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await queue[index]();
    }
  };
  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
};
